import * as React from "react";
import { Alert, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMe, useSetActiveOrg } from "@/api/hooks";
import { useOrgLegal } from "@/api/legal";
import { clearOrgTermsRequired } from "@/features/legal/orgTermsGate";
import { OrgSwitchButton } from "@/features/legal/orgTermsShared";
import { useOrgName } from "@/features/legal/orgTermsText";

/** A lapsed org, shown without blocking (spec §9.1).
 *
 *  Members: "Your organisation's admin needs to accept the updated
 *  terms…", the admins by name, and the org switcher. The app stays
 *  readable; writes answer 428. Admins see it on the pages the gate
 *  leaves open (deleting the org, billing) and for another org a write
 *  reached: "Review and accept" takes them to that org's gate. */
export function OrgTermsBlockedBanner({
  slug,
  isAdmin,
}: {
  slug: string;
  isAdmin: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const legal = useOrgLegal(slug);
  const name = useOrgName(slug);
  const navigate = useNavigate();
  const setActive = useSetActiveOrg();
  const admins = legal.data?.admins ?? [];
  const active = me.data?.admin_scope.active_org_slug === slug;
  const review = () => {
    if (active) {
      navigate("/app/sessions");
      return;
    }
    setActive.mutate({ slug }, { onSuccess: () => clearOrgTermsRequired() });
  };
  return (
    <Alert
      severity="warning"
      data-testid="org-terms-banner"
      // On a phone the actions go under the text instead of squeezing it.
      sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, "& .MuiAlert-message": { flex: "1 1 220px" } }}
      action={
        <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {isAdmin && (
            <Button color="inherit" size="small" onClick={review} disabled={setActive.isPending}>
              {t("legal.org.review")}
            </Button>
          )}
          <OrgSwitchButton slug={slug} variant="text" color="inherit" />
        </Stack>
      }
    >
      {isAdmin
        ? t("legal.org.blocked_admin", { org: name })
        : admins.length > 0
          ? t("legal.org.blocked", { admins: admins.join(", ") })
          : t("legal.org.blocked_short")}
    </Alert>
  );
}
